import React, { Fragment, memo, useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useSelector } from 'react-redux';
import htm from 'htm';

import { ResizableInspector } from './inspector-components.js';
import {
  OPERATORS,
  DATE_FIELDS,
  FLAT_FIELD_SCHEMA,
} from './inspector-schema.js';
import { formatDateTime, formatNumber, toNumber, wrapText } from './inspector-utils.js';
import ControlPanel from './Controls.jsx';
import {
  CONNECTION_LABELS,
  CONTROL_PANEL_MIN_WIDTH,
  CONTROL_PANEL_MAX_WIDTH,
  DEFAULT_CONTROL_PANEL_WIDTH,
} from './controlConstants.js';

import { selectAuthState } from '../app/store/authSlice.js';
import { validateApiKey } from './apiKeyUtils.js';
import useSessionStorageState from '../hooks/useSessionStorageState.js';

const html = htm.bind(React.createElement);


const DEFAULT_COMBINATOR = 'and';

function isRuleGroupEntry(entry) {
  return Boolean(entry && typeof entry === 'object' && Array.isArray(entry.rules));
}

function createIdentifier(prefix) {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `${prefix}-${Date.now()}-${Math.random().toString(16).slice(2, 10)}`;
}

function createEmptyFilterGroup() {
  return {
    id: createIdentifier('fg'),
    combinator: DEFAULT_COMBINATOR,
    rules: [],
  };
}

function createEmptyRule() {
  return normalizeRule({
    id: createIdentifier('fr'),
    field: '',
    operator: '',
    value: '',
  });
}

const OPERATOR_META = Object.values(OPERATORS)
  .flat()
  .reduce((acc, operator) => {
    if (!acc[operator.value]) {
      acc[operator.value] = {
        label: operator.label,
        noValue: Boolean(operator.noValue),
        range: Boolean(operator.range),
        mapKey: Boolean(operator.mapKey),
        mapKeyValue: Boolean(operator.mapKeyValue),
      };
    }
    return acc;
  }, {});

const ALL_OPERATORS = Object.entries(OPERATOR_META).map(([value, meta]) => ({
  name: value,
  value,
  label: meta.label || value,
  arity: meta.noValue ? 'unary' : meta.range || meta.mapKeyValue ? 2 : 1,
}));

function getOperatorMeta(operator) {
  return OPERATOR_META[operator] || { label: operator, noValue: false, range: false, mapKey: false, mapKeyValue: false };
}

function normalizeRule(rule) {
  if (!rule) {
    return null;
  }

  const operatorMeta = getOperatorMeta(rule.operator);
  let value = rule.value;

  if (operatorMeta.noValue) {
    value = '';
  } else if (operatorMeta.range || operatorMeta.mapKeyValue) {
    if (!Array.isArray(value)) {
      value = ['', ''];
    } else {
      value = [value[0] ?? '', value[1] ?? ''];
    }
  } else if (Array.isArray(value)) {
    value = value[0] ?? '';
  } else if (value === undefined || value === null) {
    value = '';
  }

  return {
    ...rule,
    id: rule.id || createIdentifier('fr'),
    value,
  };
}

function flattenRules(group) {
  if (!group || !Array.isArray(group.rules)) {
    return [];
  }
  return group.rules.flatMap((entry) => (isRuleGroupEntry(entry) ? flattenRules(entry) : [entry]));
}

function hasActiveFilters(group) {
  return flattenRules(group).some((rule) => {
    if (!rule) return false;
    const field = rule.field || '';
    if (!field || field === '~') {
      return false;
    }
    const operator = rule.operator || '';
    const meta = getOperatorMeta(operator);
    if (meta.noValue) {
      return true;
    }
    if (meta.range || meta.mapKeyValue) {
      const value = Array.isArray(rule.value) ? rule.value : [];
      return value.some((entry) => Boolean(entry && String(entry).trim()));
    }
    return Boolean(rule.value && String(rule.value).trim());
  });
}

// Convert filters to simple match format for backend (equality only)
function filtersToMatchString(group) {
  const rules = flattenRules(group);
  if (!rules.length) {
    return '';
  }

  return rules
    .filter((rule) => rule.field && rule.operator === 'equals' && typeof rule.value === 'string' && rule.value.trim() !== '')
    .map((rule) => `${rule.field}:${rule.value}`)
    .join(',');
}


function getNestedValue(source, path) {
  if (!source || !path) {
    return undefined;
  }

  const segments = path.split('.');
  let current = source;

  for (const segment of segments) {
    if (current === null || current === undefined) {
      return undefined;
    }

    if (!segment) {
      continue;
    }

    if (segment.endsWith('[]')) {
      const key = segment.slice(0, -2);
      current = current?.[key];
      if (!Array.isArray(current)) {
        return undefined;
      }
      continue;
    }

    const arrayMatch = segment.match(/^(.*)\[(\d+)\]$/);
    if (arrayMatch) {
      const [, key, rawIndex] = arrayMatch;
      const index = Number(rawIndex);
      const collection = key ? current?.[key] : current;
      if (!Array.isArray(collection) || Number.isNaN(index) || index < 0 || index >= collection.length) {
        return undefined;
      }
      current = collection[index];
      continue;
    }

    current = current?.[segment];
  }

  return current;
}

function someMatch(candidate, predicate) {
  if (Array.isArray(candidate)) {
    return candidate.some((entry) => someMatch(entry, predicate));
  }
  return predicate(candidate);
}

function coerceNumber(value) {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  const numeric = Number(value);
  return Number.isNaN(numeric) ? null : numeric;
}

function coerceTimestamp(value) {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  if (value instanceof Date) {
    const millis = value.getTime();
    return Number.isNaN(millis) ? null : millis;
  }
  if (typeof value === 'number') {
    return Number.isNaN(value) ? null : value;
  }
  const parsed = Date.parse(value);
  if (!Number.isNaN(parsed)) {
    return parsed;
  }
  const numeric = Number(value);
  return Number.isNaN(numeric) ? null : numeric;
}

function toLowerCase(value) {
  return String(value ?? '').toLowerCase();
}

function getFieldType(fieldPath) {
  if (!fieldPath) {
    return 'string';
  }
  const schema = FLAT_FIELD_SCHEMA[fieldPath];
  if (schema && schema.type) {
    return schema.type;
  }
  // Check if it's a timestamp field by name
  if (DATE_FIELDS.some(f => fieldPath.toLowerCase().includes(f.toLowerCase()))) {
    return 'timestamp';
  }
  return 'string';
}

function evaluateRule(rule, event) {
  if (!rule || !rule.field || rule.field === '~' || !rule.operator) {
    return true;
  }

  const meta = getOperatorMeta(rule.operator);
  const fieldValue = getNestedValue(event.raw, rule.field);
  const fieldType = getFieldType(rule.field);

  const ruleValue = meta.range || meta.mapKeyValue ? (Array.isArray(rule.value) ? rule.value : [rule.value, '']) : rule.value;

  switch (rule.operator) {
    case 'equals':
      return someMatch(fieldValue, (candidate) => candidate != null && String(candidate) === String(ruleValue ?? ''));
    case 'notEquals': {
      const matches = someMatch(fieldValue, (candidate) => candidate != null && String(candidate) === String(ruleValue ?? ''));
      return !matches;
    }
    case 'contains': {
      const needle = toLowerCase(ruleValue);
      if (!needle) return true;
      return someMatch(fieldValue, (candidate) => candidate != null && toLowerCase(candidate).includes(needle));
    }
    case 'startsWith': {
      const prefix = toLowerCase(ruleValue);
      if (!prefix) return true;
      return someMatch(fieldValue, (candidate) => candidate != null && toLowerCase(candidate).startsWith(prefix));
    }
    case 'endsWith': {
      const suffix = toLowerCase(ruleValue);
      if (!suffix) return true;
      return someMatch(fieldValue, (candidate) => candidate != null && toLowerCase(candidate).endsWith(suffix));
    }
    case 'regex': {
      if (!ruleValue) return true;
      try {
        const regex = new RegExp(String(ruleValue), 'i');
        return someMatch(fieldValue, (candidate) => candidate != null && regex.test(String(candidate)));
      } catch (err) {
        return false;
      }
    }
    case 'gt': {
      const threshold = coerceNumber(ruleValue);
      if (threshold === null) return false;
      return someMatch(fieldValue, (candidate) => {
        const numeric = coerceNumber(candidate);
        return numeric !== null && numeric > threshold;
      });
    }
    case 'lt': {
      const threshold = coerceNumber(ruleValue);
      if (threshold === null) return false;
      return someMatch(fieldValue, (candidate) => {
        const numeric = coerceNumber(candidate);
        return numeric !== null && numeric < threshold;
      });
    }
    case 'gte': {
      const threshold = coerceNumber(ruleValue);
      if (threshold === null) return false;
      return someMatch(fieldValue, (candidate) => {
        const numeric = coerceNumber(candidate);
        return numeric !== null && numeric >= threshold;
      });
    }
    case 'lte': {
      const threshold = coerceNumber(ruleValue);
      if (threshold === null) return false;
      return someMatch(fieldValue, (candidate) => {
        const numeric = coerceNumber(candidate);
        return numeric !== null && numeric <= threshold;
      });
    }
    case 'between': {
      const [minRaw, maxRaw] = Array.isArray(ruleValue) ? ruleValue : [ruleValue, undefined];
      const parser = fieldType === 'timestamp' ? coerceTimestamp : coerceNumber;
      const min = parser(minRaw);
      const max = parser(maxRaw);
      return someMatch(fieldValue, (candidate) => {
        const comparable = parser(candidate);
        if (comparable === null) return false;
        if (min !== null && comparable < min) return false;
        if (max !== null && comparable > max) return false;
        return true;
      });
    }
    case 'exists':
      return someMatch(fieldValue, (candidate) => candidate !== null && candidate !== undefined && !(typeof candidate === 'string' && candidate.trim() === ''));
    case 'isTrue':
      return someMatch(fieldValue, (candidate) => candidate === true || toLowerCase(candidate) === 'true');
    case 'isFalse':
      return someMatch(fieldValue, (candidate) => candidate === false || toLowerCase(candidate) === 'false');
    case 'after': {
      const baseline = coerceTimestamp(ruleValue);
      if (baseline === null) return false;
      return someMatch(fieldValue, (candidate) => {
        const timestamp = coerceTimestamp(candidate);
        return timestamp !== null && timestamp > baseline;
      });
    }
    case 'before': {
      const baseline = coerceTimestamp(ruleValue);
      if (baseline === null) return false;
      return someMatch(fieldValue, (candidate) => {
        const timestamp = coerceTimestamp(candidate);
        return timestamp !== null && timestamp < baseline;
      });
    }
    case 'hasKey': {
      const key = String(Array.isArray(ruleValue) ? ruleValue[0] : ruleValue || '').trim();
      if (!key) return false;
      return someMatch(fieldValue, (candidate) => candidate && typeof candidate === 'object' && key in candidate);
    }
    case 'keyEquals': {
      const [rawKey, rawExpected] = Array.isArray(ruleValue) ? ruleValue : [ruleValue, undefined];
      const key = String(rawKey || '').trim();
      if (!key) return false;
      const expected = rawExpected === undefined ? '' : String(rawExpected);
      return someMatch(fieldValue, (candidate) => {
        if (!candidate || typeof candidate !== 'object') {
          return false;
        }
        const resolved = candidate[key];
        return resolved !== undefined && String(resolved) === expected;
      });
    }
    default:
      return true;
  }
}

function evaluateRuleGroup(group, event) {
  if (!group || !Array.isArray(group.rules) || !group.rules.length) {
    return true;
  }

  const results = group.rules.map((entry) => (isRuleGroupEntry(entry) ? evaluateRuleGroup(entry, event) : evaluateRule(entry, event)));
  const combinator = (group.combinator || DEFAULT_COMBINATOR).toLowerCase();
  const base = combinator === 'or' ? results.some(Boolean) : results.every(Boolean);
  return group.not ? !base : base;
}


function matchesSearch(event, searchQuery) {
  if (!searchQuery || !searchQuery.trim()) {
    return true;
  }

  // Parse search query into terms
  const terms = parseSearchQuery(searchQuery);
  
  // All terms must match (AND logic)
  return terms.every(term => evaluateSearchTerm(event, term));
}

/**
 * Parse search query into individual search terms
 */
function parseSearchQuery(query) {
  const terms = [];
  let current = '';
  let inQuotes = false;
  let escaped = false;
  
  for (let i = 0; i < query.length; i++) {
    const char = query[i];
    
    if (escaped) {
      current += char;
      escaped = false;
      continue;
    }
    
    if (char === '\\') {
      escaped = true;
      continue;
    }
    
    if (char === '"') {
      if (inQuotes) {
        // End of quoted section
        if (current) {
          terms.push({ type: 'exact', value: current, negative: false });
          current = '';
        }
        inQuotes = false;
      } else {
        // Start of quoted section
        if (current) {
          terms.push(parseSearchTerm(current));
          current = '';
        }
        inQuotes = true;
      }
      continue;
    }
    
    if (!inQuotes && char === ' ') {
      if (current) {
        terms.push(parseSearchTerm(current));
        current = '';
      }
      continue;
    }
    
    current += char;
  }
  
  if (current) {
    terms.push(inQuotes ? { type: 'exact', value: current, negative: false } : parseSearchTerm(current));
  }
  
  return terms;
}

/**
 * Parse individual search term
 */
function parseSearchTerm(term) {
  // Check for negative search
  const negative = term.startsWith('-') || term.startsWith('!');
  if (negative) {
    term = term.substring(1);
  }
  
  // Check for field:value or field=value pattern
  const fieldMatch = term.match(/^([a-zA-Z0-9_.[\]]+)([:|=><])(.*)$/);
  if (fieldMatch) {
    const [, field, operator, value] = fieldMatch;
    return {
      type: 'field',
      field,
      operator: operator === ':' || operator === '=' ? 'contains' : operator,
      value,
      negative
    };
  }
  
  // Check for wildcards
  if (term.includes('*') || term.includes('?')) {
    return {
      type: 'wildcard',
      pattern: term,
      negative
    };
  }
  
  // Default to contains search
  return {
    type: 'contains',
    value: term,
    negative
  };
}

/**
 * Evaluate a single search term against an event
 */
function evaluateSearchTerm(event, term) {
  let matches = false;
  
  switch (term.type) {
    case 'exact':
      matches = searchInEvent(event, term.value, true);
      break;
      
    case 'contains':
      matches = searchInEvent(event, term.value, false);
      break;
      
    case 'field':
      matches = searchInField(event, term.field, term.value, term.operator);
      break;
      
    case 'wildcard':
      matches = searchWithWildcard(event, term.pattern);
      break;
      
    default:
      matches = searchInEvent(event, term.value, false);
  }
  
  return term.negative ? !matches : matches;
}

/**
 * Search for a value anywhere in the event
 */
function searchInEvent(event, searchValue, exact = false) {
  const needle = searchValue.toLowerCase();
  
  // Search in basic event metadata
  const metadataFields = [
    event.eventId,
    event.eventType,
    event.partition,
    event.region,
    event.name,
    event.userId,
    event.anonymousId
  ];
  
  for (const field of metadataFields) {
    if (field) {
      const value = String(field).toLowerCase();
      if (exact ? value === needle : value.includes(needle)) {
        return true;
      }
    }
  }
  
  // Search in summary (efficiently)
  if (event.summary && searchInObject(event.summary, needle, exact)) {
    return true;
  }
  
  // Search in raw payload (efficiently)
  if (event.raw && searchInObject(event.raw, needle, exact)) {
    return true;
  }
  
  return false;
}

/**
 * Recursively search in an object without JSON.stringify
 */
function searchInObject(obj, needle, exact = false) {
  if (obj === null || obj === undefined) {
    return false;
  }
  
  // Handle primitive values
  if (typeof obj !== 'object') {
    const value = String(obj).toLowerCase();
    return exact ? value === needle : value.includes(needle);
  }
  
  // Handle arrays
  if (Array.isArray(obj)) {
    return obj.some(item => searchInObject(item, needle, exact));
  }
  
  // Handle objects
  for (const key in obj) {
    // Check key name
    if (key.toLowerCase().includes(needle)) {
      return true;
    }
    
    // Check value
    if (searchInObject(obj[key], needle, exact)) {
      return true;
    }
  }
  
  return false;
}

/**
 * Search in a specific field with optional operator
 */
function searchInField(event, fieldPath, searchValue, operator = 'contains') {
  // Get field value using existing helper
  const fieldValue = getNestedValue(event.raw, fieldPath) ?? 
                     getNestedValue(event.summary, fieldPath) ??
                     getNestedValue(event, fieldPath);
  
  if (fieldValue === undefined || fieldValue === null) {
    return false;
  }
  
  const needle = searchValue.toLowerCase();
  const value = String(fieldValue).toLowerCase();
  
  switch (operator) {
    case '>':
      return parseFloat(fieldValue) > parseFloat(searchValue);
    case '<':
      return parseFloat(fieldValue) < parseFloat(searchValue);
    case '=':
    case ':':
    case 'contains':
    default:
      return value.includes(needle);
  }
}

/**
 * Search with wildcard pattern (* and ?)
 */
function searchWithWildcard(event, pattern) {
  // Convert wildcard pattern to regex
  const regexPattern = pattern
    .toLowerCase()
    .replace(/[.+^${}()|[\]\\]/g, '\\$&') // Escape regex special chars
    .replace(/\*/g, '.*') // * matches any characters
    .replace(/\?/g, '.'); // ? matches single character
    
  try {
    const regex = new RegExp(regexPattern);
    
    // Search in all text values
    return searchWithRegex(event, regex);
  } catch (e) {
    // Invalid regex, fall back to simple contains
    return searchInEvent(event, pattern.replace(/[*?]/g, ''), false);
  }
}

/**
 * Search with a regex pattern
 */
function searchWithRegex(event, regex) {
  // Check basic fields
  const fields = [
    event.eventId,
    event.eventType, 
    event.partition,
    event.region,
    event.name,
    event.userId,
    event.anonymousId
  ];
  
  for (const field of fields) {
    if (field && regex.test(String(field).toLowerCase())) {
      return true;
    }
  }
  
  // Check in objects
  return (
    (event.summary && searchObjectWithRegex(event.summary, regex)) ||
    (event.raw && searchObjectWithRegex(event.raw, regex))
  );
}

/**
 * Recursively search in object with regex
 */
function searchObjectWithRegex(obj, regex) {
  if (obj === null || obj === undefined) {
    return false;
  }
  
  if (typeof obj !== 'object') {
    return regex.test(String(obj).toLowerCase());
  }
  
  if (Array.isArray(obj)) {
    return obj.some(item => searchObjectWithRegex(item, regex));
  }
  
  for (const key in obj) {
    if (regex.test(key.toLowerCase()) || searchObjectWithRegex(obj[key], regex)) {
      return true;
    }
  }
  
  return false;
}

// API Key validation
const useGlobalCredentials = () => {
  const { apiKey, writeKey, isAuthenticated } = useSelector(selectAuthState);

  return useMemo(
    () => ({
      apiKey: typeof apiKey === 'string' ? apiKey.trim() : '',
      writeKey: typeof writeKey === 'string' ? writeKey.trim() : '',
      isAuthenticated: Boolean(
        isAuthenticated && typeof apiKey === 'string' && apiKey.trim(),
      ),
    }),
    [apiKey, writeKey, isAuthenticated],
  );
};

function parseMatches(value) {
  if (!value) {
    return {};
  }
  return value
    .split(',')
    .map((entry) => entry.trim())
    .filter(Boolean)
    .reduce((acc, pair) => {
      const [key, val] = pair.split(':', 2);
      if (key && val) {
        acc[key.trim()] = val.trim();
      }
      return acc;
    }, {});
}

function ensureId(raw) {
  return (
    raw.event_gid ||
    raw.eventId ||
    raw.message_id ||
    raw.session_gid ||
    raw.session_id ||
    raw.entity_gid ||
    (typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : `evt-${Date.now()}-${Math.random()}`)
  );
}

function extractTimestamp(raw) {
  for (const field of DATE_FIELDS) {
    const value = raw[field];
    if (value !== null && value !== undefined) {
      if (typeof value === 'number') {
        return value;
      }
      const parsed = Date.parse(value);
      if (!Number.isNaN(parsed)) {
        return parsed;
      }
    }
  }
  return null;
}

function toDate(millis) {
  if (millis === null || millis === undefined) {
    return null;
  }
  const date = new Date(millis);
  return Number.isNaN(date.getTime()) ? null : date;
}

function formatRelativeTime(millis) {
  const date = toDate(millis);
  if (!date) {
    return '';
  }
  const diff = Date.now() - date.getTime();
  const abs = Math.abs(diff);
  const minutes = Math.round(abs / 60000);
  if (minutes < 1) {
    return diff >= 0 ? 'just now' : 'in moments';
  }
  if (minutes < 60) {
    return diff >= 0 ? `${minutes}m ago` : `in ${minutes}m`;
  }
  const hours = Math.round(minutes / 60);
  if (hours < 24) {
    return diff >= 0 ? `${hours}h ago` : `in ${hours}h`;
  }
  const days = Math.round(hours / 24);
  return diff >= 0 ? `${days}d ago` : `in ${days}d`;
}

function formatCurrency(total, currency) {
  if (total === null || total === undefined) {
    return null;
  }
  if (currency) {
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency,
        maximumFractionDigits: 2,
      }).format(total);
    } catch (err) {
      // fall back to generic formatting
    }
  }
  return `${formatNumber(total)}${currency ? ` ${currency}` : ''}`.trim();
}

function resolveEventUser(raw) {
  return (
    raw.user_id ||
    raw.user_gid ||
    raw.userId ||
    (typeof raw.user === 'string' ? raw.user : null) ||
    raw.anonymous_id ||
    raw.anonymous_gid ||
    raw.account_id ||
    raw.session_id ||
    raw.session_gid ||
    null
  );
}

function resolveLocation(raw) {
  if (!raw || !Array.isArray(raw.location) || raw.location.length === 0) {
    return null;
  }

  const loc = raw.location[0];

  // For US locations: "City, ST" (e.g., "San Francisco, CA")
  if (loc.country_code === 'US' && loc.locality && loc.region) {
    return `${loc.locality}, ${loc.region}`;
  }

  // For international: "City, Country" (e.g., "London, UK")
  if (loc.locality && loc.country_code) {
    return `${loc.locality}, ${loc.country_code}`;
  }

  // Just city
  if (loc.locality) {
    return loc.locality;
  }

  // Region and country (e.g., "California, US")
  if (loc.region && loc.country_code) {
    return `${loc.region}, ${loc.country_code}`;
  }

  // Just country code
  if (loc.country_code) {
    return loc.country_code;
  }

  // Country name as fallback
  if (loc.country) {
    return loc.country;
  }

  return null;
}

function resolveMeaningfulProperty(raw) {
  if (!raw) {
    return '—';
  }

  // Get event type to determine most meaningful property
  const eventType = raw.type || raw.event_type || 'track';

  // Get products from either raw.products or raw.commerce.products
  const products = Array.isArray(raw.products)
    ? raw.products
    : (Array.isArray(raw.commerce?.products) ? raw.commerce.products : null);

  // Priority 1: Single product (most specific) - applies to any event type
  if (products && products.length === 1) {
    const product = products[0];
    const productDetail = product.name || product.product || product.product_id || product.sku;
    if (productDetail) {
      return productDetail;
    }
  }

  // Priority 2: Commerce order/cart ID (very specific)
  if (raw.commerce) {
    const commerceDetail = raw.commerce.order_id ||
      raw.commerce.checkout_id ||
      raw.commerce.cart_id;
    if (commerceDetail) {
      return commerceDetail;
    }
  }

  // Priority 3: Event type-specific properties
  switch (eventType) {
    case 'page':
      // For page events, show page title, URL, or path (check both raw.page and raw.context.page)
      const page = raw.page || raw.context?.page;
      return page?.title || page?.url || page?.path || resolveEventUser(raw) || '—';

    case 'screen':
      // For screen events, show screen name (check both raw.screen and raw.context.screen)
      const screen = raw.screen || raw.context?.screen;
      return screen?.name || resolveEventUser(raw) || '—';

    case 'identify':
      // For identify events, show user ID or traits
      return raw.user_id || raw.userId || raw.traits?.email || raw.traits?.name || '—';

    case 'group':
      // For group events, show account/group ID
      return raw.account_id || raw.group_id || raw.groupId || resolveEventUser(raw) || '—';

    case 'alias':
      // For alias events, show the alias mapping
      {
        const from = raw.previous_id || raw.previousId;
        const to = raw.user_id || raw.userId;
        if (from && to) {
          return `${from} → ${to}`;
        }
        return from || to || '—';
      }
  }

  // Priority 4: Multiple products - show first product
  if (products && products.length > 0) {
    const product = products[0];
    const productDetail = product.name || product.product || product.product_id || product.sku;
    if (productDetail) {
      return productDetail;
    }
  }

  // Final fallback: user identifier or dash
  return resolveEventUser(raw) || '—';
}

function normalizeSemanticEvent(raw) {
  const id = ensureId(raw);
  const timestamp = extractTimestamp(raw);
  const eventName = raw.event || raw.event_name || raw.type || 'Semantic event';
  const eventType = raw.type || raw.event_type || 'event';

  const sentiments = Array.isArray(raw.sentiment)
    ? raw.sentiment
        .filter((entry) => entry && (entry.sentiment || entry.reason))
        .map((entry) => ({
          label: entry.sentiment || 'Sentiment',
          reason: entry.reason || entry.target_entity || entry.target_type || null,
        }))
    : [];

  const classification = Array.isArray(raw.classification)
    ? raw.classification
        .filter((entry) => entry && entry.value)
        .map((entry) => ({
          label: entry.value,
        }))
    : [];

  const firstLocation = Array.isArray(raw.location) && raw.location.length > 0 ? raw.location[0] : null;
  const locationLabel = firstLocation
    ? [firstLocation.locality, firstLocation.region, firstLocation.country]
        .filter(Boolean)
        .join(', ')
    : null;

  const commerce = raw.commerce || null;
  const commerceSummary = commerce
    ? {
        total:
          toNumber(commerce.total) ??
          toNumber(commerce.revenue) ??
          toNumber(commerce.subtotal) ??
          null,
        currency: commerce.currency || null,
        orderId: commerce.order_id || commerce.checkout_id || commerce.cart_id || null,
      }
    : null;

  const summaryHighlights = [];
  if (locationLabel) {
    summaryHighlights.push({ label: 'Location', value: locationLabel });
  }
  if (commerceSummary && commerceSummary.total !== null) {
    summaryHighlights.push({
      label: 'Order Value',
      value: formatCurrency(commerceSummary.total, commerceSummary.currency),
    });
  }
  if (classification.length) {
    summaryHighlights.push({ label: 'Classification', value: classification[0].label });
  }
  if (sentiments.length) {
    summaryHighlights.push({ label: 'Sentiment', value: sentiments[0].label });
  }

  return {
    id,
    raw,
    name: eventName,
    eventType,
    timestamp,
    timestampLabel: formatDateTime(timestamp),
    relativeTime: formatRelativeTime(timestamp),
    summaryHighlights,
  };
}

const EventRow = memo(
  function EventRow({ event, index, isSelected, isPinned, onSelect }) {
    const location = resolveLocation(event.raw);
    const detail = resolveMeaningfulProperty(event.raw);

    // Debug logging
    if (!detail || detail === '—') {
      console.log('[EventRow] Missing detail for event:', {
        eventName: event.name,
        eventType: event.eventType,
        hasRaw: !!event.raw,
        rawKeys: event.raw ? Object.keys(event.raw).slice(0, 10) : [],
        detail,
        sampleRaw: event.raw ? {
          type: event.raw.type,
          user_id: event.raw.user_id,
          anonymous_id: event.raw.anonymous_id,
          hasProducts: Array.isArray(event.raw.products),
          hasCommerce: !!event.raw.commerce,
          hasPage: !!event.raw.page,
        } : null
      });
    }

    return html`
      <tr className=${isSelected ? 'active' : ''} onClick=${() => onSelect(event.id, event, index)}>
        <td className="as-col-event">
          ${isPinned ? html`<span className="as-pin-indicator" title="Pinned in place">📌 </span>` : null}
          ${event.name}
        </td>
        <td className="as-col-type">${event.eventType ?? '—'}</td>
        <td className="as-col-location">${location ?? '—'}</td>
        <td className="as-col-time">${event.timestampLabel}</td>
        <td className="as-col-detail">${wrapText(detail)}</td>
      </tr>
    `;
  },
  (prev, next) => prev.event === next.event && prev.isSelected === next.isSelected && prev.isPinned === next.isPinned && prev.index === next.index
);

function EventsPanel({
  events,
  selectedId,
  selectedEventSnapshot,
  sortConfig,
  onSort,
  onSelectEvent,
  emptyMessage,
  inspectorView,
  onInspectorViewChange,
  onCopy,
  isInspectorCollapsed,
  onInspectorCollapse,
}) {
  const selectedEvent = useMemo(() => {
    // Use the snapshot if available, otherwise find the live event
    if (selectedEventSnapshot) {
      return selectedEventSnapshot.event;
    }
    return events.find((item) => item.id === selectedId) || null;
  }, [events, selectedId, selectedEventSnapshot]);

  const shouldScroll = events.length > 30;

  return html`
    <section className="as-stream" aria-label="Events Stream">
      <div className="as-stream-grid">
        <div className="as-events-wrapper ${shouldScroll ? 'as-scrollable' : ''}">
          <header className="as-events-header">
            <h3>Events</h3>
          </header>
          ${events.length === 0 ? html`<p className="as-empty-state">${emptyMessage}</p>` : null}
          <table className="as-events-table" hidden=${events.length === 0}>
            <thead>
              <tr>
                <th scope="col" onClick=${() => onSort('event')} style=${{ cursor: 'pointer' }}>
                  Event ${sortConfig.field === 'event' ? (sortConfig.direction === 'desc' ? '↓' : '↑') : ''}
                </th>
                <th scope="col" onClick=${() => onSort('type')} style=${{ cursor: 'pointer' }}>
                  Type ${sortConfig.field === 'type' ? (sortConfig.direction === 'desc' ? '↓' : '↑') : ''}
                </th>
                <th scope="col" onClick=${() => onSort('location')} style=${{ cursor: 'pointer' }}>
                  Location ${sortConfig.field === 'location' ? (sortConfig.direction === 'desc' ? '↓' : '↑') : ''}
                </th>
                <th scope="col" onClick=${() => onSort('timestamp')} style=${{ cursor: 'pointer' }}>
                  Timestamp ${sortConfig.field === 'timestamp' ? (sortConfig.direction === 'desc' ? '↓' : '↑') : ''}
                </th>
                <th scope="col" onClick=${() => onSort('detail')} style=${{ cursor: 'pointer' }}>
                  Detail ${sortConfig.field === 'detail' ? (sortConfig.direction === 'desc' ? '↓' : '↑') : ''}
                </th>
              </tr>
            </thead>
            <tbody aria-live="polite">
              ${events.map((event, index) =>
                html`<${EventRow}
                  key=${event.id}
                  event=${event}
                  index=${index}
                  isSelected=${event.id === selectedId}
                  isPinned=${event.id === selectedId && selectedId !== null}
                  onSelect=${onSelectEvent}
                />`
              )}
            </tbody>
          </table>
        </div>
        <${ResizableInspector}
          event=${selectedEvent}
          view=${inspectorView}
          onViewChange=${onInspectorViewChange}
          onCopy=${onCopy}
          hasEvents=${events.length > 0}
          isCollapsed=${isInspectorCollapsed}
          onCollapse=${onInspectorCollapse}
        />
      </div>
    </section>
  `;
}

function ActivityStreamApp({
  onStoredKeyUsed = () => {},
  externalStream = null,
} = {}) {
  const { apiKey, writeKey, isAuthenticated } = useGlobalCredentials();

  const [filters, setFilters] = useState(() => createEmptyFilterGroup());
  const [eventTypeFilter, setEventTypeFilter] = useState([]);
  const [search, setSearch] = useState('');
  const [limit, setLimit] = useState(50);
  const [sortConfig, setSortConfig] = useState({ field: 'timestamp', direction: 'desc' });

  // When using external stream, we still manage our own events/metrics/topicAvailable
  // because the external stream has different event structure (not normalized)
  // We only use external stream for connection status
  const [connection, setConnection] = useState('disconnected');
  const [status, setStatus] = useState({ message: '', tone: 'info' });
  const [events, setEvents] = useState([]);
  const [metrics, setMetrics] = useState({ total: 0, lastEventAt: null });
  const [topicAvailable, setTopicAvailable] = useState(false);

  const [selectedId, setSelectedId] = useState(null);
  const [selectedEventSnapshot, setSelectedEventSnapshot] = useState(null);
  const MAX_BUFFER_SIZE = 1000;
  const [inspectorView, setInspectorView] = useState('details');
  const [controlPanelWidth, setControlPanelWidth] = useSessionStorageState(
    'activity-stream-control-width',
    DEFAULT_CONTROL_PANEL_WIDTH
  );
  const [pausedEventIds, setPausedEventIds] = useState(new Set());
  const [isManuallyPaused, setIsManuallyPaused] = useState(false);
  const [isInspectorCollapsed, setIsInspectorCollapsed] = useSessionStorageState(
    'activity-stream-inspector-collapsed',
    false
  );
  const [isControlPanelCollapsed, setIsControlPanelCollapsed] = useSessionStorageState(
    'activity-stream-control-collapsed',
    false
  );

  const controllerRef = useRef(null);
  const decoderRef = useRef(null);
  const mountedRef = useRef(true);

  if (!decoderRef.current) {
    decoderRef.current = new TextDecoder('utf-8');
  }

  const headerStatusMessage = status?.message ?? '';
  const headerStatusTone = status?.tone ?? 'info';

  // Apply filters to events for display (keep full buffer for field discovery)
  const filteredEvents = useMemo(() => {
    let result = events;

    // Hide events that arrived while paused
    if (pausedEventIds.size > 0) {
      result = result.filter(event => {
        // Always show selected/pinned events
        if (event.id === selectedId) {
          return true;
        }
        // Hide events that came during pause
        return !pausedEventIds.has(event.id);
      });
    }

    // Apply event type filter (if any types selected)
    if (eventTypeFilter.length > 0) {
      result = result.filter(event =>
        eventTypeFilter.includes(event.eventType) || event.id === selectedId
      );
    }

    // Apply field filters
    if (hasActiveFilters(filters)) {
      result = result.filter(event => {
        if (event.id === selectedId) {
          return true;
        }
        return evaluateRuleGroup(filters, event);
      });
    }

  // Apply search filter (client-side for immediate feedback)
  if (search.trim()) {
    result = result.filter(event => {
      if (event.id === selectedId) {
        return true; // Always show selected/pinned events
      }
      return matchesSearch(event, search);
    });
  }

    // Apply sorting
    result = [...result].sort((a, b) => {
      let aVal, bVal;

      switch (sortConfig.field) {
        case 'timestamp':
          aVal = a.timestamp || 0;
          bVal = b.timestamp || 0;
          break;
        case 'event':
          aVal = a.name || '';
          bVal = b.name || '';
          break;
        case 'type':
          aVal = a.eventType || '';
          bVal = b.eventType || '';
          break;
        case 'location':
          aVal = resolveLocation(a.raw) || '';
          bVal = resolveLocation(b.raw) || '';
          break;
        case 'detail':
          aVal = resolveMeaningfulProperty(a.raw) || '';
          bVal = resolveMeaningfulProperty(b.raw) || '';
          break;
        case 'user':
          aVal = resolveEventUser(a.raw) || '';
          bVal = resolveEventUser(b.raw) || '';
          break;
        default:
          return 0;
      }

      if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });

    // If there's a pinned event, keep it at fixed position in the array
    if (selectedEventSnapshot) {
      // Remove the live version of selected event if it exists
      result = result.filter(e => e.id !== selectedId);

      // Insert the snapshot at its pinned position
      const pinnedIndex = selectedEventSnapshot.pinnedIndex;
      const insertAt = Math.min(pinnedIndex, result.length);
      result.splice(insertAt, 0, selectedEventSnapshot.event);
    }

    // Apply display limit (after filtering and sorting)
    if (result.length > limit) {
      result = result.slice(0, limit);
    }

    return result;
  }, [events, eventTypeFilter, filters, selectedId, selectedEventSnapshot, sortConfig, limit, search, pausedEventIds]);

  const updateStatus = useCallback((message, tone = 'info') => {
    setStatus({ message, tone });
  }, []);

  const resetState = useCallback(() => {
    setEvents([]);
    setMetrics({ total: 0, lastEventAt: null });
    setSelectedId(null);
    setSelectedEventSnapshot(null);
    setInspectorView('details');
    setTopicAvailable(false);
    setIsManuallyPaused(false);
    setPausedEventIds(new Set());
  }, []);

  const handleControlPanelResize = useCallback((nextWidth) => {
    setControlPanelWidth((previous) => {
      const numericWidth = Math.round(Number(nextWidth));
      if (Number.isNaN(numericWidth)) {
        return previous;
      }
      const clamped = Math.min(
        CONTROL_PANEL_MAX_WIDTH,
        Math.max(CONTROL_PANEL_MIN_WIDTH, numericWidth)
      );
      return clamped === previous ? previous : clamped;
    });
  }, []);

  const stopStream = useCallback((reason = 'user') => {
    const controller = controllerRef.current;
    if (controller) {
      controller.abortReason = reason;
      controllerRef.current = null;
      try {
        controller.abort();
      } catch (err) {
        // ignore abort errors
      }
    }
  }, []);

  useEffect(() => {
    return () => {
      mountedRef.current = false;
      stopStream('unmount');
    };
  }, [stopStream]);

  // Sync external stream state to internal state
  useEffect(() => {
    if (!externalStream) return;

    // Sync connection status
    setConnection(externalStream.connection);
    setStatus(externalStream.status);
    setTopicAvailable(externalStream.topicAvailable);
  }, [
    externalStream,
    externalStream?.connection,
    externalStream?.status,
    externalStream?.topicAvailable,
  ]);

  const handleConnectionEvent = useCallback(
    (payload) => {
      if (!payload || typeof payload !== 'object' || !mountedRef.current) {
        return;
      }
      const statusValue = payload.status ?? 'connected';
      if (statusValue === 'connected') {
        setTopicAvailable(true);
        setConnection('connected');
        updateStatus(payload.message ?? '', 'success');
        return;
      }
      if (statusValue === 'topic-missing') {
        setTopicAvailable(false);
        setConnection('connected');
        updateStatus(payload.message ?? 'Waiting for events...', 'warning');
        return;
      }
      if (statusValue === 'error') {
        setTopicAvailable(false);
        setConnection('error');
        updateStatus(payload.message ?? 'Stream error', 'error');
      }
    },
    [updateStatus]
  );

  const appendEvent = useCallback(
    (payload, eventName = 'semantic-event') => {
      console.log('[ActivityStream] appendEvent called:', { eventName, payloadKeys: Object.keys(payload || {}) });

      if (eventName === 'status' || eventName === 'connection') {
        console.log('[ActivityStream] Handling as connection event');
        handleConnectionEvent(payload);
        return;
      }
      if (eventName === 'heartbeat' || eventName === 'ping') {
        console.log('[ActivityStream] Ignoring heartbeat/ping');
        return;
      }
      if (
        payload &&
        typeof payload === 'object' &&
        payload.status &&
        (payload.topic || payload.topicAvailable !== undefined)
      ) {
        console.log('[ActivityStream] Detected status payload, handling as connection event');
        handleConnectionEvent(payload);
        return;
      }

      console.log('[ActivityStream] Normalizing semantic event...');
      const normalized = normalizeSemanticEvent(payload);
      console.log('[ActivityStream] Normalized event:', { id: normalized.id, name: normalized.name, eventType: normalized.eventType });

      // If paused, track this event ID
      if (isManuallyPaused) {
        setPausedEventIds(prev => new Set(prev).add(normalized.id));
      }

      setEvents((prev) => {
        const next = [normalized, ...prev];
        // Keep up to MAX_BUFFER_SIZE events in memory
        if (next.length > MAX_BUFFER_SIZE) {
          next.length = MAX_BUFFER_SIZE;
        }
        return next;
      });
      setMetrics((prev) => ({
        total: prev.total + 1,
        lastEventAt: normalized.timestamp ?? prev.lastEventAt,
      }));
      // Don't auto-select events
    },
    [MAX_BUFFER_SIZE, handleConnectionEvent, isManuallyPaused]
  );

  // Process external stream events through our appendEvent to normalize them
  const lastProcessedEventCount = useRef(0);
  useEffect(() => {
    if (!externalStream || !externalStream.events) return;

    const currentEventCount = externalStream.events.length;
    if (currentEventCount > lastProcessedEventCount.current) {
      // New events arrived - process only the new ones
      const newEvents = externalStream.events.slice(0, currentEventCount - lastProcessedEventCount.current);
      newEvents.reverse().forEach((event) => {
        // External stream events have type and raw properties
        appendEvent(event.raw || event, event.type || 'semantic-event');
      });
      lastProcessedEventCount.current = currentEventCount;
    }
  }, [externalStream, externalStream?.events, appendEvent]);

  const connect = useCallback(async () => {
    const trimmedApiKey = apiKey?.trim() ?? '';
    console.log('[ActivityStream] Connect attempt - API key present:', Boolean(trimmedApiKey));
    if (!trimmedApiKey) {
      updateStatus('No API key configured', 'warning');
      console.warn('[ActivityStream] No API key configured');
      return;
    }

    const validation = validateApiKey(trimmedApiKey);
    if (!validation.valid) {
      updateStatus(validation.message, 'error');
      console.error('[ActivityStream] API key validation failed:', validation.message);
      return;
    }
    console.log('[ActivityStream] Starting connection...');

    onStoredKeyUsed?.();

    stopStream('reconnect');
    resetState();
    setConnection('connecting');
    updateStatus('');

    const params = new URLSearchParams();
    const matchString = filtersToMatchString(filters);
    const matches = parseMatches(matchString);
    if (Object.keys(matches).length > 0) {
      params.set(
        'match',
        Object.entries(matches)
          .map(([key, value]) => `${key}:${value}`)
          .join(',')
      );
    }
    // Search is now client-side only for better responsiveness
    // Don't send search param to backend
    params.set('limit', String(limit));

    const controller = new AbortController();
    controller.abortReason = null;
    controllerRef.current = controller;

    try {
      const baseUrl = import.meta.env?.VITE_ACTIVITY_API_BASE_URL || '/api/activity';
      const url = `${baseUrl}/stream?${params.toString()}`;
      console.log('[ActivityStream] Connecting to:', url);
      const response = await fetch(url, {
        method: 'GET',
        headers: (() => {
          const headers = {
            'x-api-key': trimmedApiKey,
          };
          const trimmedWriteKey = writeKey?.trim();
          if (trimmedWriteKey) {
            headers.writekey = trimmedWriteKey;
          }
          console.log('[ActivityStream] Request headers:', { ...headers, 'x-api-key': '***' });
          return headers;
        })(),
        cache: 'no-store',
        signal: controller.signal,
      });

      console.log('[ActivityStream] Response status:', response.status, response.ok);
      if (!response.ok || !response.body) {
        throw new Error(`Stream failed: ${response.status}`);
      }

      if (!mountedRef.current) {
        return;
      }

      updateStatus('Handshake established; waiting for broker status…', 'info');

      const reader = response.body.getReader();
      let bufferText = '';
      const decoder = decoderRef.current;

      while (true) {
        const { value, done } = await reader.read();
        if (done) {
          break;
        }
        bufferText += decoder.decode(value, { stream: true });
        const segments = bufferText.split('\n\n');
        bufferText = segments.pop() ?? '';

        segments.forEach((segment) => {
          if (!segment.trim()) {
            return;
          }
          let eventName = 'message';
          const dataLines = [];
          segment.split('\n').forEach((line) => {
            if (line.startsWith('event:')) {
              eventName = line.slice(6).trim();
            } else if (line.startsWith('data:')) {
              dataLines.push(line.slice(5).trim());
            }
          });

          if (!dataLines.length) {
            return;
          }

          const payloadText = dataLines.join('\n');

          if (eventName === 'heartbeat' || eventName === 'ping') {
            return;
          }

          try {
            const payload = JSON.parse(payloadText);
            console.log('[ActivityStream] Received event:', eventName, 'payload keys:', Object.keys(payload));
            appendEvent(payload, eventName);
            if (eventName !== 'heartbeat' && eventName !== 'ping') {
              setConnection('connected');
            }
            if (eventName === 'semantic-event') {
              setTopicAvailable(true);
            }
          } catch (err) {
            console.error(`[ActivityStream] Unable to parse ${eventName} payload`, err);
          }
        });
      }

      if (!controller.abortReason) {
        updateStatus('Stream closed by server', 'warning');
        setConnection('disconnected');
      }
    } catch (error) {
      const abortReason = controller.abortReason;
      if (abortReason) {
        if (abortReason === 'pause') {
          setConnection('paused');
          updateStatus('Stream paused', 'info');
        } else if (abortReason === 'disconnect') {
          setConnection('disconnected');
          updateStatus('Disconnected', 'info');
        } else if (abortReason === 'input-clear') {
          setConnection('disconnected');
          updateStatus('Disconnected', 'info');
        } else if (abortReason === 'reconnect' || abortReason === 'unmount') {
          // handled by the next connection/unmount cleanup
        } else {
          setConnection('disconnected');
          updateStatus('Stream stopped', 'warning');
        }
      } else {
        console.error(error);
        setConnection('error');
        updateStatus('Failed to connect to stream', 'error');
      }
    } finally {
      if (controllerRef.current === controller) {
        controllerRef.current = null;
      }
    }
  }, [
    appendEvent,
    handleConnectionEvent,
    limit,
    filters,
    resetState,
    stopStream,
    updateStatus,
    apiKey,
    writeKey,
    onStoredKeyUsed,
  ]);

  const handlePause = useCallback(() => {
    if (externalStream) {
      externalStream.pause();
      setIsManuallyPaused(true);
      return;
    }
    if (connection !== 'connected') {
      return;
    }
    setConnection('paused');
    setIsManuallyPaused(true);
    updateStatus('Stream paused - events buffering in background', 'info');
    // Note: We don't stop the stream, it continues receiving events
  }, [connection, updateStatus, externalStream]);

  const handleResume = useCallback(() => {
    if (externalStream) {
      externalStream.resume();
      setIsManuallyPaused(false);
      setPausedEventIds(new Set());
      return;
    }
    if (connection !== 'paused') {
      return;
    }
    setConnection('connected');
    setIsManuallyPaused(false);
    setPausedEventIds(new Set()); // Clear the paused events, showing them now
    updateStatus('Stream resumed', 'success');
  }, [connection, updateStatus, externalStream]);

  const handleDisconnect = useCallback(() => {
    // When using external stream, disconnect button should do nothing
    // User must select "Offline" to disconnect
    if (externalStream) {
      return;
    }
    stopStream('disconnect');
    resetState();
    setConnection('disconnected');
    updateStatus('Disconnected', 'info');
  }, [resetState, stopStream, updateStatus, externalStream]);

  const handleEventTypeToggle = useCallback((type) => {
    setEventTypeFilter(prev => {
      if (prev.includes(type)) {
        return prev.filter(t => t !== type);
      } else {
        return [...prev, type];
      }
    });
  }, []);

  const handleSearchInput = useCallback((event) => {
    setSearch(event.currentTarget.value);
  }, []);

  const handleLimitInput = useCallback(
    (event) => {
      const value = parseInt(event.currentTarget.value, 10);
      setLimit(value);
      // Don't truncate the events buffer - limit only affects display
    },
    []
  );

  const handleSort = useCallback((field) => {
    setSortConfig(prev => ({
      field,
      direction: prev.field === field && prev.direction === 'desc' ? 'asc' : 'desc'
    }));
  }, []);

  const handleSelectEvent = useCallback((id, event, index) => {
    // If clicking the same event, unpin/unselect it
    if (selectedId === id) {
      setSelectedEventSnapshot(null);
      setSelectedId(null);
      return;
    }

    // Create a snapshot with the current position
    if (event && index >= 0) {
      setSelectedEventSnapshot({
        event: event,
        pinnedIndex: index
      });
      setSelectedId(id);
    }
  }, [selectedId]);

  const handleInspectorViewChange = useCallback((view) => {
    setInspectorView(view);
  }, []);

  const handleCopySelected = useCallback(() => {
    const entry = events.find((item) => item.id === selectedId);
    if (!entry) {
      return;
    }
    const json = JSON.stringify(entry.raw, null, 2);
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(json).then(
        () => updateStatus('Event copied to clipboard', 'success'),
        () => updateStatus('Copy failed – select JSON manually', 'warning')
      );
    } else {
      updateStatus('Clipboard API unavailable – copy from inspector manually', 'warning');
    }
  }, [events, selectedId, updateStatus]);

  const handleExport = useCallback(() => {
    if (!events.length) {
      return;
    }
    const payload = events.map((entry) => entry.raw);
    const blob = new Blob([JSON.stringify(payload, null, 2)], {
      type: 'application/json',
    });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
    anchor.download = `activity-stream-${timestamp}.json`;
    anchor.click();
    URL.revokeObjectURL(url);
  }, [events]);

  // Clear selection if selected event is no longer in the list
  useEffect(() => {
    if (!events.length) {
      setSelectedId(null);
      setSelectedEventSnapshot(null);
      return;
    }
    if (selectedId && !events.some((item) => item.id === selectedId)) {
      setSelectedId(null);
      setSelectedEventSnapshot(null);
    }
  }, [events, selectedId]);

  const connectionLabel = CONNECTION_LABELS[connection] ?? CONNECTION_LABELS.disconnected;

  const emptyMessage = useMemo(() => {
    if (connection === 'connecting') {
      return 'Connecting…';
    }
    if (connection === 'connected') {
      return topicAvailable ? 'Streaming events…' : 'Waiting for events…';
    }
    if (connection === 'paused') {
      const bufferedCount = pausedEventIds.size;
      return bufferedCount > 0
        ? `Stream paused. ${bufferedCount} event${bufferedCount === 1 ? '' : 's'} buffered. Click Resume to show them.`
        : 'Stream paused. Events are being buffered in the background.';
    }
    if (connection === 'error') {
      return 'Stream unavailable. Check broker status and retry.';
    }
    return 'Connect with a valid API key to begin streaming events.';
  }, [connection, topicAvailable, pausedEventIds.size]);

  const hasKey = Boolean(apiKey?.trim());
  const isConnecting = connection === 'connecting';
  const isConnected = connection === 'connected';
  const isConnectionPaused = connection === 'paused';

  const canPause = isConnected;
  const canResume = isConnectionPaused;

  const layoutStyle = useMemo(() => {
    if (isControlPanelCollapsed) {
      return {};
    }
    return {
      '--as-control-panel-width': `${controlPanelWidth}px`,
    };
  }, [controlPanelWidth, isControlPanelCollapsed]);

  return html`
    <div className="as-app">
      <main
        className="as-layout ${isControlPanelCollapsed ? 'as-control-panel-collapsed' : ''}"
        style=${layoutStyle}
      >
        <${ControlPanel}
          onPause=${handlePause}
          onResume=${handleResume}
          connection=${connection}
          eventTypeFilter=${eventTypeFilter}
          onEventTypeChange=${handleEventTypeToggle}
          search=${search}
          onSearchChange=${handleSearchInput}
          limit=${limit}
          onLimitChange=${handleLimitInput}
          canPause=${canPause}
          canResume=${canResume}
          isCollapsed=${isControlPanelCollapsed}
          onCollapse=${setIsControlPanelCollapsed}
          panelWidth=${controlPanelWidth}
          onPanelWidthChange=${handleControlPanelResize}
          onConnect=${connect}
          onDisconnect=${handleDisconnect}
          hideConnectionControls=${Boolean(externalStream)}
          hasKey=${hasKey}
          onExport=${handleExport}
          eventCount=${events.length}
          statusMessage=${headerStatusMessage}
          statusTone=${headerStatusTone}
        />
        <${EventsPanel}
          events=${filteredEvents}
          selectedId=${selectedId}
          selectedEventSnapshot=${selectedEventSnapshot}
          sortConfig=${sortConfig}
          onSort=${handleSort}
          onSelectEvent=${handleSelectEvent}
          emptyMessage=${emptyMessage}
          inspectorView=${inspectorView}
          onInspectorViewChange=${handleInspectorViewChange}
          onCopy=${handleCopySelected}
          isInspectorCollapsed=${isInspectorCollapsed}
          onInspectorCollapse=${setIsInspectorCollapsed}
        />
      </main>
    </div>
  `;
}

export default ActivityStreamApp;
